// Utility to handle chunk loading errors
export class ChunkErrorHandler {
  private static retryCount = 0;
  private static maxRetries = 3;

  static handleChunkError(error: any): boolean {
    const isChunkError =
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.name === 'ChunkLoadError';

    if (isChunkError) {
      // Silent handling - just reload, no scary warnings
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;

        // Clear any cached chunks and reload
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => {
              if (name.includes('chunk') || name.includes('assets')) {
                caches.delete(name);
              }
            });
          });
        }

        // Add a small delay before retrying
        setTimeout(() => {
          window.location.reload();
        }, 500);

        return true; // Error was handled
      } else {
        // Max retries - force reload after clearing all caches
        this.clearAllCaches().then(() => {
          window.location.reload();
        });
        return true; // Error was handled
      }
    }

    return false; // Error was not a chunk error
  }

  static async clearAllCaches(): Promise<void> {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        // Silent - no console logs
      } catch (error) {
        // Silent error handling
      }
    }
  }

  static resetRetryCount(): void {
    this.retryCount = 0;
  }

  static setupGlobalErrorHandler(): void {
    // Handle unhandled promise rejections (including chunk errors)
    window.addEventListener('unhandledrejection', (event) => {
      const handled = this.handleChunkError(event.reason);
      if (handled) {
        event.preventDefault(); // Prevent the error from being logged to console
      }
    });

    // Handle regular errors
    window.addEventListener('error', (event) => {
      const handled = this.handleChunkError(event.error);
      if (handled) {
        event.preventDefault();
      }
    });

    // Reset retry count on successful navigation
    window.addEventListener('beforeunload', () => {
      this.resetRetryCount();
    });
  }
}

// Initialize global error handling
if (typeof window !== 'undefined') {
  ChunkErrorHandler.setupGlobalErrorHandler();
}